import { Box, Stack, Typography } from "@mui/material";

import { Ref, useImperativeHandle, useEffect, forwardRef } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { UseAttributeSchemaObjectStateType } from "./AttributeSchemaObjectState";
import AttributeSchemaObjectUtils from "./AttributeSchemaObjectUtils";
import { AttributeSchemaFormType, AttributeSchemaUsageActions } from "../AttributeSchemaEditorUtils";
import { AttributeSchemaObjectListItem } from "./AttributeSchemaObjectListItem";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { IAttribute, AttributeSchemaTypeEnum, IAttributeValueOption } from "../../../services/attribute-data-service/Models";
import { BaseActionEnum } from "../../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import NoData from "../../no-data/NoData";
import SideSheetFilterInput from "../../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetMultiSelect from "../../side-sheet/side-sheet-multiselect/SideSheetMultiSelect";
import SideSheetSwitch from "../../side-sheet/side-sheet-switch/SideSheetSwitch";
import SideSheet from "../../side-sheet/side-sheet/SideSheet";
import VirtualizedList, { VirtualizedListItem } from "../../virtualized-list/VirtualizedList";



export type AttributeSchemaObjectListType = {
    executeAction: (action: AttributeSchemaUsageActions | BaseActionEnum) => void;
};

export type AttributeSchemaObjectItem = IAttribute & VirtualizedListItem;

export type AttributeSchemaObjectListProps = {
    objectType: eLynxObjectTypeEnum;
    openSideSheet?: boolean;
    onCloseSideSheet?: () => void;
    setViewOptionState?: (value: ViewOptionStateEnum) => void;
    schemaUsageState: UseAttributeSchemaObjectStateType;
};

function AttributeSchemaObjectList({
    objectType,
    openSideSheet,
    onCloseSideSheet,
    setViewOptionState,
    schemaUsageState
}: AttributeSchemaObjectListProps, ref: Ref<unknown> | undefined) {

    const isPhone = isMobile && !isTablet;
    const {t} = useTranslation();

    useImperativeHandle<unknown, AttributeSchemaObjectListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const {
        state, 
        setViewSelected,
        setObjectFilter,
        setValueFilter,
        resetState
    } = schemaUsageState;

    const {
        getValues,
        watch,
        //control,
        //reset,
        setValue,
        trigger
        //resetField
    } = useFormContext<AttributeSchemaFormType>();    


    const valueOptions = getValues('valueOptions');

    const name = objectType === eLynxObjectTypeEnum.Device ? 
        "devices" : 
        objectType === eLynxObjectTypeEnum.Well ? 
        "wells" : 
        objectType === eLynxObjectTypeEnum.Tank ? 
        'tanks' : 
        'tankBatteries';

    const [
        availableObjects,
        schemaType,
        decimals,
        wholeDigits
    ] = watch([
        name,
        'schemaType',
        'decimalDigits',
        'wholeDigits'
    ]);
   
    const enableViewSelected = (availableObjects?.filter(o => o.selected)?.length) > 0;

    const filteredObjects = AttributeSchemaObjectUtils.filter(availableObjects, state.objectNameFilter, state.valueOptionFilter);

    function updateCheckState(checked: boolean) {

        const filteredItems = AttributeSchemaObjectUtils.filter(availableObjects, state.objectNameFilter, state.valueOptionFilter);

        filteredItems.forEach(o => {

            const ndx = availableObjects.findIndex(v => v.oid === o.oid);
            setValue(`${name}.${ndx}.selected`, checked, {shouldDirty: true});
        });

        trigger(name);
    }

    function executeAction(action: AttributeSchemaUsageActions | BaseActionEnum) {

        switch(action) {
            case AttributeSchemaUsageActions.SelectAll:
                updateCheckState(true);
                break;

            case AttributeSchemaUsageActions.RemoveAll:
                updateCheckState(false);
                break;
        }
    }

    function renderItem(_index: number, item: IAttribute) {


        return (
            <AttributeSchemaObjectListItem
                name={name}
                item={item}
                key={`${item.oid}`}
            />
        );
    };


    function onViewSelected(value: boolean) {
        setViewSelected(value);
    }


    function onSetFilter(value: string) {
        setObjectFilter(value);
    }


    function onFilterItem(item: AttributeSchemaObjectItem): boolean {

        return state.viewSelected ? item.selected === true : true;

        // let show = !!item.odt.match(filterRegEx);


        // if (show && state.viewSelected) {
        //     show = item.selected === true;
        // }

        // if (show && state.valueOptionFilter.length > 0) {

        //     show = state.valueOptionFilter.find(o => o.value === item.avalt) !== undefined;
        // }

        // return show;
    }

    useEffect(() => {

        if (!enableViewSelected) {
            setViewSelected(false);
        }

    }, [
        enableViewSelected,
        setViewSelected
    ]);
    
    useEffect(() => {
        setViewOptionState && setViewOptionState(state.viewOptionState);
    },[
        state.viewOptionState,
        setViewOptionState
    ]);


    return (
        <Stack
            direction='row'
            sx={{
                display: 'flex',
                flex: '1 1 auto'
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 1 auto',
                    flexDirection: 'column',
                    overflowY: 'auto'
                }}>

                {schemaType === AttributeSchemaTypeEnum.FreeformText &&
                    <Typography variant='subtitle1' sx={{
                        padding: 1
                    }}>
                        {`Enter up to 128 characters of free form text.`}
                    </Typography>
                }

                {schemaType === AttributeSchemaTypeEnum.PredefinedValue &&
                    <Typography variant='subtitle1' sx={{
                        padding: 1
                    }}>
                        {`Choose a value from the list of options.`}
                    </Typography>
                }


                {schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText &&
                    <Typography variant='subtitle1' sx={{
                        padding: 1
                    }}>
                        {`Choose a value from the list of options or enter up to 128 characters of free form text.`}
                    </Typography>
                }


                {schemaType === AttributeSchemaTypeEnum.Numeric &&
                    <Typography variant='subtitle1' sx={{
                        padding: 1
                    }}>
                        {`Enter numeric values with up to ${wholeDigits} whole digits and ${decimals} decimals.`}
                    </Typography>
                }
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden"
                    }}>

                    {filteredObjects.length === 0 &&         
                        <Box sx={{
                            padding: 1
                        }}>
                            <NoData text={t('setpointAdmin.nodata.noFilterMatch', {name: 'objects'})} />
                        </Box>
                    }
                                            
                    <VirtualizedList
                        items={filteredObjects}
                        renderItem={renderItem}
                        filter={onFilterItem}
                    />

                </Box>
            </Box>
            <SideSheet
                title={t("options")}
                open={openSideSheet ?? false}
                hideCloseButton={!isPhone}
                onClose={() => onCloseSideSheet && onCloseSideSheet()}
                onReset={resetState}
                >

                <SideSheetSwitch

                    label={t("attributeSchema.viewApplied", {name: getObjectTypeName(objectType, true)})}
                    checked={state.viewSelected}
                    onChange={(checked: boolean) => {
                        onViewSelected(checked);
                    }}
                    disabled={!enableViewSelected} />

                <SideSheetFilterInput
                    label={t("setpointAdmin.filterByObject", {objectType: getObjectTypeName(objectType)})}
                    value={state.objectNameFilter}
                    onChange={(value) => {
                        onSetFilter(value);
                    }} 
                    disabled={state.viewSelected}/>


                {
                    (schemaType === AttributeSchemaTypeEnum.PredefinedValue || schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) &&

                    <SideSheetMultiSelect<IAttributeValueOption>
                        label={t("attributeSchema.filterByValue")}
                        options={valueOptions}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={state.valueOptionFilter}
                        getOptionLabel={(option) => option.value}
                        onChange={(_ev, value) => {
                            setValueFilter(value)
                        }}
                    />

                }


            </SideSheet>
        </Stack>
    );
}

export default forwardRef(AttributeSchemaObjectList);